import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft,
  BadgeCheck,
  Banknote,
  Calendar,
  Check,
  CircleDollarSign,
  Gift,
  Loader2,
  Send,
  ShieldCheck,
  X,
  XCircle,
} from 'lucide-react'
import { Link, useParams } from 'react-router-dom'

import { PageHeader, PageHeaderToolbar } from '@/components/app/PageHeader'
import { SortableTableHead, type SortDirection } from '@/components/app/SortableTableHead'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAuthSession } from '@/features/auth/session'
import { KpiCard } from '@/features/dashboard/components/KpiCard'
import { useAppBreadcrumbTrail } from '@/layouts/AppShell'
import { useTranslation } from 'react-i18next'
import { ApiError } from '@/lib/api'
import { cn } from '@/lib/utils'
import { getAppLocale } from '@/lib/locale'
import { ExchangeCancellationModal } from '../components/ExchangeCancellationModal'
import { ExchangeRejectionModal } from '../components/ExchangeRejectionModal'

import {
  approveExchangeRequest,
  cancelExchangeRequest,
  completeExchangeRequest,
  fetchExchangeRequest,
  markExchangeRequestProcessing,
  rejectExchangeRequest,
} from '../api'
import type { ExchangeRequestStatus } from '@/types/exchanges'

type TimelineSortKey = 'date'
type LedgerSortKey = 'effective'

function getStatusPresentation(t: (key: string) => string): Record<
  ExchangeRequestStatus,
  { label: string; className: string }
> {
  return {
    requested: {
      label: t('exchanges.status.requested'),
      className:
        'border-transparent bg-blue-500/15 text-blue-800 dark:bg-blue-500/20 dark:text-blue-300',
    },
    approved: {
      label: t('exchanges.status.approved'),
      className:
        'border-transparent bg-amber-500/15 text-amber-900 dark:bg-amber-500/20 dark:text-amber-300',
    },
    rejected: {
      label: t('exchanges.status.rejected'),
      className:
        'border-transparent bg-rose-500/15 text-rose-800 dark:bg-rose-500/20 dark:text-rose-300',
    },
    processing: {
      label: t('exchanges.status.processing'),
      className:
        'border-transparent bg-indigo-500/15 text-indigo-800 dark:bg-indigo-500/20 dark:text-indigo-300',
    },
    completed: {
      label: t('exchanges.status.completed'),
      className:
        'border-transparent bg-emerald-500/15 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300',
    },
    cancelled: {
      label: t('exchanges.status.cancelled'),
      className: 'border-transparent bg-muted text-muted-foreground',
    },
  }
}

function formatDate(value: string | null, withTime = false, t: (key: string) => string) {
  if (!value) return t('common.notAvailable')

  return new Date(value).toLocaleString(getAppLocale(), {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    ...(withTime
      ? {
          hour: '2-digit',
          minute: '2-digit',
        }
      : {}),
  })
}

function formatCurrency(amount: number, currencyCode: string) {
  return new Intl.NumberFormat(getAppLocale(), {
    style: 'currency',
    currency: currencyCode,
    maximumFractionDigits: 2,
  }).format(amount)
}

function toTimestamp(value: string | null) {
  if (!value) return Number.NEGATIVE_INFINITY
  const parsed = new Date(value).getTime()
  return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed
}

function requestTypeLabel(type: 'reward' | 'cash', t: (key: string) => string) {
  return type === 'reward' ? t('exchanges.requestType.reward') : t('exchanges.requestType.cash')
}

function requestTitle(exchange: {
  request_type: 'reward' | 'cash'
  requested_reward_title: string | null
  exchange_pack_item_title: string | null
}, t: (key: string) => string) {
  return exchange.request_type === 'reward'
    ? exchange.requested_reward_title ?? exchange.exchange_pack_item_title ?? t('exchanges.request.rewardDefault')
    : t('exchanges.request.cashDefault')
}

function invalidateExchangeState(queryClient: ReturnType<typeof useQueryClient>) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: ['exchange-requests'] }),
    queryClient.invalidateQueries({ queryKey: ['points', 'summary'] }),
    queryClient.invalidateQueries({ queryKey: ['points', 'by-program'] }),
    queryClient.invalidateQueries({ queryKey: ['points', 'ledger'] }),
  ])
}

/* ── Status Stepper ── */

type StepState = 'completed' | 'active' | 'pending' | 'error'

interface WorkflowStep {
  key: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  date: string | null
  actor: string | null
  state: StepState
}

function StatusStepper({ steps, t }: { steps: WorkflowStep[]; t: (key: string) => string }) {
  return (
    <div className="relative">
      <div className="flex items-start justify-between gap-2">
        {steps.map((step, index) => (
          <div key={step.key} className="relative flex flex-1 flex-col items-center">
            {index > 0 && (
              <div
                className={cn(
                  'absolute right-1/2 top-5 h-0.5 w-full -translate-y-1/2',
                  step.state === 'completed' || step.state === 'active'
                    ? 'bg-emerald-500 dark:bg-emerald-400'
                    : step.state === 'error'
                      ? 'bg-rose-500 dark:bg-rose-400'
                      : 'bg-border'
                )}
              />
            )}

            <div
              className={cn(
                'relative z-10 flex size-10 items-center justify-center rounded-full text-sm',
                step.state === 'completed' && 'bg-emerald-500 text-white dark:bg-emerald-400',
                step.state === 'active' && 'bg-blue-500 text-white dark:bg-blue-400',
                step.state === 'pending' && 'border-2 border-border bg-background text-muted-foreground',
                step.state === 'error' && 'bg-rose-500 text-white dark:bg-rose-400'
              )}
            >
              {step.state === 'completed' ? (
                <Check className="size-5" />
              ) : step.state === 'error' ? (
                <X className="size-5" />
              ) : (
                <step.icon className="size-5" />
              )}
            </div>

            <p
              className={cn(
                'mt-3 text-center text-sm font-semibold',
                step.state === 'completed' || step.state === 'active'
                  ? 'text-foreground'
                  : step.state === 'error'
                    ? 'text-rose-600 dark:text-rose-400'
                    : 'text-muted-foreground'
              )}
            >
              {step.label}
            </p>

            {step.date ? (
              <p className="mt-1 text-center text-xs text-muted-foreground">
                {formatDate(step.date, true, t)}
              </p>
            ) : (
              <p className="mt-1 text-center text-xs text-muted-foreground/50">—</p>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

/* ── Skeleton ── */

function ExchangeDetailSkeleton() {
  return (
    <section className="app-section">
      {/* Header */}
      <div className="flex flex-col gap-2 pb-1.5 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
        <div className="flex items-center gap-2">
          <Skeleton className="h-8 w-8 rounded-lg" />
          <Skeleton className="h-6 w-52 rounded-md" />
          <Skeleton className="h-5 w-20 rounded-full" />
          <Skeleton className="h-5 w-16 rounded-full" />
        </div>
        <div className="flex flex-wrap gap-2">
          <Skeleton className="h-8 w-28 rounded-md" />
          <Skeleton className="h-8 w-28 rounded-md" />
          <Skeleton className="h-8 w-28 rounded-md" />
          <Skeleton className="h-8 w-24 rounded-md" />
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="rounded-lg bg-card p-4 md:p-6">
            <div className="flex items-center gap-2.5">
              <Skeleton className="size-8 rounded-md" />
              <Skeleton className="h-4 w-24" />
            </div>
            <Skeleton className="mt-4 h-8 w-28" />
            <Skeleton className="mt-4 h-3.5 w-36" />
          </div>
        ))}
      </div>

      {/* Status Stepper */}
      <div className="rounded-lg bg-card app-card-padding">
        <Skeleton className="mb-6 h-5 w-48 rounded-md" />
        <div className="flex items-start justify-between gap-2">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="flex flex-1 flex-col items-center">
              <Skeleton className="size-10 rounded-full" />
              <Skeleton className="mt-3 h-3 w-16" />
              <Skeleton className="mt-1 h-2.5 w-20" />
            </div>
          ))}
        </div>
      </div>

      {/* Timeline Table */}
      <div className="rounded-lg bg-card app-card-padding">
        <div className="mb-5 flex items-start justify-between">
          <div className="space-y-2">
            <Skeleton className="h-5 w-36 rounded-md" />
            <Skeleton className="h-4 w-56 rounded-md" />
          </div>
          <Skeleton className="h-3.5 w-16 rounded-md" />
        </div>
        <div className="overflow-hidden rounded-lg border border-border">
          <div className="h-10 bg-muted/30" />
          {Array.from({ length: 4 }).map((_, index) => (
            <div
              key={index}
              className={cn(
                'flex items-center gap-3 px-4 py-3',
                index % 2 === 1 && 'bg-muted/15',
                index > 0 && 'border-t border-border/50',
              )}
            >
              <Skeleton className="h-4 w-24 rounded-md" />
              <Skeleton className="h-4 w-32 rounded-md" />
              <Skeleton className="h-4 w-48 rounded-md" />
              <Skeleton className="ml-auto h-4 w-28 rounded-md" />
            </div>
          ))}
        </div>
      </div>

      {/* Ledger Table */}
      <div className="rounded-lg bg-card app-card-padding">
        <div className="mb-5 flex items-start justify-between">
          <div className="space-y-2">
            <Skeleton className="h-5 w-32 rounded-md" />
            <Skeleton className="h-4 w-48 rounded-md" />
          </div>
          <Skeleton className="h-3.5 w-16 rounded-md" />
        </div>
        <div className="overflow-hidden rounded-lg border border-border">
          <div className="h-10 bg-muted/30" />
          {Array.from({ length: 4 }).map((_, index) => (
            <div
              key={index}
              className={cn(
                'flex items-center gap-3 px-4 py-3',
                index % 2 === 1 && 'bg-muted/15',
                index > 0 && 'border-t border-border/50',
              )}
            >
              <Skeleton className="h-4 w-28 rounded-md" />
              <div className="min-w-0 flex-1 space-y-1.5">
                <Skeleton className="h-4 w-32 rounded-md" />
                <Skeleton className="h-3 w-40 rounded-md" />
              </div>
              <Skeleton className="h-5 w-16 rounded-full" />
              <Skeleton className="h-4 w-20 rounded-md" />
              <Skeleton className="h-4 w-28 rounded-md" />
            </div>
          ))}
        </div>
      </div>

      {/* Catalog Table */}
      <div className="rounded-lg bg-card app-card-padding">
        <div className="mb-5 space-y-2">
          <Skeleton className="h-5 w-44 rounded-md" />
          <Skeleton className="h-4 w-64 rounded-md" />
        </div>
        <div className="overflow-hidden rounded-lg border border-border">
          <div className="h-10 bg-muted/30" />
          {Array.from({ length: 3 }).map((_, index) => (
            <div
              key={index}
              className={cn(
                'flex items-center gap-3 px-4 py-3',
                index % 2 === 1 && 'bg-muted/15',
                index > 0 && 'border-t border-border/50',
              )}
            >
              <Skeleton className="h-4 w-40 rounded-md" />
              <Skeleton className="h-4 w-24 rounded-md" />
              <Skeleton className="ml-auto h-4 w-20 rounded-md" />
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

/* ── Main Page ── */

export function ExchangeDetailPage() {
  const { t } = useTranslation()
  const { exchangeRequestId } = useParams<{ exchangeRequestId: string }>()
  const queryClient = useQueryClient()
  const { user, hasPermission } = useAuthSession()
  const canApprove = hasPermission('exchange-request.approve')
  const canReject = hasPermission('exchange-request.reject')
  const isAgentView = Boolean(user?.agent_profile)
  const [timelineSortDirection, setTimelineSortDirection] = useState<SortDirection>('desc')
  const [ledgerSortDirection, setLedgerSortDirection] = useState<SortDirection>('desc')
  const [timelineSortKey, setTimelineSortKey] = useState<TimelineSortKey>('date')
  const [ledgerSortKey, setLedgerSortKey] = useState<LedgerSortKey>('effective')
  const [rejectionModalOpen, setRejectionModalOpen] = useState(false)
  const [cancellationModalOpen, setCancellationModalOpen] = useState(false)

  const exchangeQuery = useQuery({
    queryKey: ['exchange-requests', 'detail', exchangeRequestId],
    queryFn: async () => fetchExchangeRequest(exchangeRequestId ?? ''),
    enabled: Boolean(exchangeRequestId),
  })

  const approveMutation = useMutation({
    mutationFn: async () => approveExchangeRequest(exchangeRequestId ?? ''),
    onSuccess: async () => {
      await invalidateExchangeState(queryClient)
    },
  })

  const rejectMutation = useMutation({
    mutationFn: async ({ reason, notes }: { reason: string; notes: string | null }) =>
      rejectExchangeRequest(exchangeRequestId ?? '', { rejection_reason: reason, rejection_notes: notes }),
    onSuccess: async () => {
      await invalidateExchangeState(queryClient)
      setRejectionModalOpen(false)
    },
  })

  const processingMutation = useMutation({
    mutationFn: async () => markExchangeRequestProcessing(exchangeRequestId ?? ''),
    onSuccess: async () => {
      await invalidateExchangeState(queryClient)
    },
  })

  const completeMutation = useMutation({
    mutationFn: async () => completeExchangeRequest(exchangeRequestId ?? ''),
    onSuccess: async () => {
      await invalidateExchangeState(queryClient)
    },
  })

  const cancelMutation = useMutation({
    mutationFn: async ({ reason, notes }: { reason: string; notes: string | null }) =>
      cancelExchangeRequest(exchangeRequestId ?? '', {
        cancellation_reason: reason,
        cancellation_notes: notes,
      }),
    onSuccess: async () => {
      await invalidateExchangeState(queryClient)
      setCancellationModalOpen(false)
    },
  })

  const exchange = exchangeQuery.data?.data ?? null

  useAppBreadcrumbTrail(
    exchange
      ? [
          { label: t('exchanges.detail.breadcrumb'), to: '/payouts' },
          { label: requestTitle(exchange, t) },
        ]
      : null,
  )

  const status = exchange ? getStatusPresentation(t)[exchange.status] : getStatusPresentation(t).requested
  const hasPendingMutation =
    approveMutation.isPending ||
    rejectMutation.isPending ||
    processingMutation.isPending ||
    completeMutation.isPending ||
    cancelMutation.isPending

  const canCancel =
    !!exchange &&
    exchange.requested_by_user_id === user?.id &&
    ['requested', 'approved', 'processing'].includes(exchange.status)

  /* ── Workflow Stepper Data ── */
  const workflowSteps = useMemo<WorkflowStep[]>(() => {
    if (!exchange) return []

    const isRejected = exchange.status === 'rejected'
    const isCancelled = exchange.status === 'cancelled'
    const isCompleted = exchange.status === 'completed'
    const isProcessing = exchange.status === 'processing'
    const isApproved = exchange.status === 'approved'

    const steps: WorkflowStep[] = [
      {
        key: 'requested',
        label: t('exchanges.detail.timelineEvents.requested.step'),
        icon: Send,
        date: exchange.requested_at,
        actor: exchange.requested_by_name,
        state: 'completed',
      },
    ]

    if (isRejected) {
      steps.push({
        key: 'rejected',
        label: t('exchanges.detail.timelineEvents.rejected.step'),
        icon: X,
        date: exchange.rejected_at,
        actor: exchange.approved_by_name,
        state: 'error',
      })
      return steps
    }

    if (isCancelled) {
      steps.push({
        key: 'cancelled',
        label: t('exchanges.detail.timelineEvents.cancelled.step'),
        icon: X,
        date: exchange.cancelled_at,
        actor: exchange.requested_by_name,
        state: 'error',
      })
      return steps
    }

    steps.push({
      key: 'approved',
      label: t('exchanges.detail.timelineEvents.approved.step'),
      icon: BadgeCheck,
      date: exchange.approved_at,
      actor: exchange.approved_by_name,
      state: isApproved ? 'active' : isCompleted || isProcessing ? 'completed' : 'pending',
    })

    steps.push({
      key: 'processing',
      label: t('exchanges.detail.timelineEvents.processing.step'),
      icon: Loader2,
      date: exchange.processed_at,
      actor: exchange.approved_by_name,
      state: isProcessing ? 'active' : isCompleted ? 'completed' : 'pending',
    })

    steps.push({
      key: 'completed',
      label: t('exchanges.detail.timelineEvents.completed.step'),
      icon: Check,
      date: exchange.completed_at,
      actor: exchange.approved_by_name,
      state: isCompleted ? 'completed' : 'pending',
    })

    return steps
  }, [exchange, t])

  const timelineRows = useMemo(() => {
    if (!exchange) return []

    return [
      {
        key: 'requested',
        step: t('exchanges.detail.timelineEvents.requested.step'),
        actor: exchange.requested_by_name ?? t('exchanges.detail.timelineEvents.requested.actor'),
        detail: t('exchanges.detail.timelineEvents.requested.detail'),
        date: exchange.requested_at,
      },
      exchange.approved_at
        ? {
            key: 'approved',
            step: t('exchanges.detail.timelineEvents.approved.step'),
            actor: exchange.approved_by_name ?? t('exchanges.detail.timelineEvents.approved.actor'),
            detail: t('exchanges.detail.timelineEvents.approved.detail'),
            date: exchange.approved_at,
          }
        : null,
      exchange.processed_at
        ? {
            key: 'processing',
            step: t('exchanges.detail.timelineEvents.processing.step'),
            actor: exchange.approved_by_name ?? t('exchanges.detail.timelineEvents.processing.actor'),
            detail: t('exchanges.detail.timelineEvents.processing.detail'),
            date: exchange.processed_at,
          }
        : null,
      exchange.completed_at
        ? {
            key: 'completed',
            step: t('exchanges.detail.timelineEvents.completed.step'),
            actor: exchange.approved_by_name ?? t('exchanges.detail.timelineEvents.completed.actor'),
            detail: t('exchanges.detail.timelineEvents.completed.detail'),
            date: exchange.completed_at,
          }
        : null,
      exchange.rejected_at
        ? {
            key: 'rejected',
            step: t('exchanges.detail.timelineEvents.rejected.step'),
            actor: exchange.approved_by_name ?? t('exchanges.detail.timelineEvents.rejected.actor'),
            detail: exchange.rejection_reason
              ? `${t('exchanges.detail.timelineEvents.rejected.detail')} — ${t(`exchanges.rejectionReasons.${exchange.rejection_reason}`, { defaultValue: exchange.rejection_reason })}${exchange.rejection_notes ? `: ${exchange.rejection_notes}` : ''}`
              : t('exchanges.detail.timelineEvents.rejected.detail'),
            date: exchange.rejected_at,
          }
        : null,
      exchange.cancelled_at
        ? {
            key: 'cancelled',
            step: t('exchanges.detail.timelineEvents.cancelled.step'),
            actor: exchange.requested_by_name ?? t('exchanges.detail.timelineEvents.cancelled.actor'),
            detail: exchange.cancellation_reason
              ? `${t('exchanges.detail.timelineEvents.cancelled.detail')} — ${t(`exchanges.cancellationReasons.${exchange.cancellation_reason}`, { defaultValue: exchange.cancellation_reason })}${exchange.cancellation_notes ? `: ${exchange.cancellation_notes}` : ''}`
              : t('exchanges.detail.timelineEvents.cancelled.detail'),
            date: exchange.cancelled_at,
          }
        : null,
    ].filter(Boolean) as Array<{
      key: string
      step: string
      actor: string
      detail: string
      date: string | null
    }>
  }, [exchange])

  const ledgerRows = useMemo(() => {
    if (!exchange) return []
    return [...exchange.ledger_entries]
  }, [exchange])

  const sortedTimelineRows = useMemo(() => {
    return [...timelineRows].sort((left, right) => {
      const result = toTimestamp(left.date) - toTimestamp(right.date)
      return timelineSortDirection === 'asc' ? result : -result
    })
  }, [timelineRows, timelineSortDirection])

  const sortedLedgerRows = useMemo(() => {
    return [...ledgerRows].sort((left, right) => {
      const result = toTimestamp(left.effective_at ?? left.created_at) - toTimestamp(right.effective_at ?? right.created_at)
      return ledgerSortDirection === 'asc' ? result : -result
    })
  }, [ledgerRows, ledgerSortDirection])

  function handleTimelineSort(nextKey: TimelineSortKey) {
    if (timelineSortKey === nextKey) {
      setTimelineSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'))
      return
    }
    setTimelineSortKey(nextKey)
    setTimelineSortDirection('desc')
  }

  function handleLedgerSort(nextKey: LedgerSortKey) {
    if (ledgerSortKey === nextKey) {
      setLedgerSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'))
      return
    }
    setLedgerSortKey(nextKey)
    setLedgerSortDirection('desc')
  }

  if (!exchangeRequestId) {
    return (
      <article className="rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">
        {t('exchanges.detail.error.missingId')}
      </article>
    )
  }

  if (exchangeQuery.isPending) {
    return <ExchangeDetailSkeleton />
  }

  if (exchangeQuery.isError) {
    return (
      <article className="rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">
        {(exchangeQuery.error as ApiError).message}
      </article>
    )
  }

  if (!exchange) {
    return (
      <article className="rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">
        {t('exchanges.detail.error.notFound')}
      </article>
    )
  }

  return (
    <section className="app-section">
      {/* ── Header ── */}
      <PageHeader
        beforeTitle={
          <Button asChild variant="ghost" size="icon-sm" className="shrink-0">
            <Link to="/payouts" aria-label={t('exchanges.detail.back')}>
              <ArrowLeft className="size-4" />
            </Link>
          </Button>
        }
        title={requestTitle(exchange, t)}
        titleAddon={
          <>
            <Badge className={status.className}>{status.label}</Badge>
            <Badge variant="secondary">{requestTypeLabel(exchange.request_type, t)}</Badge>
          </>
        }
        right={
          <PageHeaderToolbar>
            {exchange.program_id ? (
              <Button asChild variant="outline" size="sm">
                <Link to={`/programs/${exchange.program_id}`}>{t('exchanges.detail.viewProgram')}</Link>
              </Button>
            ) : null}
            <Button asChild variant="outline" size="sm">
              <Link to="/commissions">{t('exchanges.detail.viewPoints')}</Link>
            </Button>
            {!isAgentView && exchange.agent_id ? (
              <Button asChild variant="outline" size="sm">
                <Link to={`/agents/${exchange.agent_id}`}>{t('exchanges.detail.viewAffiliate')}</Link>
              </Button>
            ) : null}
            {!isAgentView && <Separator orientation="vertical" className="hidden h-6 sm:block" />}
            {exchange.status === 'requested' && canApprove && !isAgentView ? (
              <ActionButton label={t('exchanges.detail.actions.approve')} busy={hasPendingMutation} onClick={() => approveMutation.mutate()} primary />
            ) : null}
            {exchange.status === 'requested' && canReject && !isAgentView ? (
              <ActionButton label={t('exchanges.detail.actions.reject')} busy={hasPendingMutation} onClick={() => setRejectionModalOpen(true)} destructive />
            ) : null}
            {exchange.status === 'approved' && canApprove && !isAgentView ? (
              <ActionButton label={t('exchanges.detail.actions.processing')} busy={hasPendingMutation} onClick={() => processingMutation.mutate()} />
            ) : null}
            {['approved', 'processing'].includes(exchange.status) && canApprove && !isAgentView ? (
              <ActionButton label={t('exchanges.detail.actions.complete')} busy={hasPendingMutation} onClick={() => completeMutation.mutate()} primary />
            ) : null}
            {canCancel ? (
              <ActionButton label={t('exchanges.detail.actions.cancel')} busy={hasPendingMutation} onClick={() => setCancellationModalOpen(true)} destructive />
            ) : null}
          </PageHeaderToolbar>
        }
      />

      {/* ── KPI Cards (clean, no badges) ── */}
      <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
        <KpiCard
          title={t('exchanges.detail.kpi.pointsRequested')}
          value={`${exchange.points_amount.toLocaleString(getAppLocale())} pts`}
          description={t('exchanges.detail.meta.points')}
          icon={CircleDollarSign}
          tone="warning"
        />
        <KpiCard
          title={t('exchanges.detail.kpi.value')}
          value={
            exchange.cash_amount === null
              ? requestTypeLabel(exchange.request_type, t)
              : formatCurrency(exchange.cash_amount, exchange.currency_code)
          }
          description={t('exchanges.detail.kpi.value')}
          icon={exchange.request_type === 'reward' ? Gift : Banknote}
          tone={exchange.request_type === 'reward' ? 'primary' : 'success'}
        />
        <KpiCard
          title={t('exchanges.detail.kpi.status')}
          value={status.label}
          description={t('exchanges.detail.meta.status')}
          icon={ShieldCheck}
          tone="info"
        />
        <KpiCard
          title={t('exchanges.detail.kpi.requestedAt')}
          value={formatDate(exchange.requested_at, true, t)}
          description={t('exchanges.detail.meta.requestedAt')}
          icon={Calendar}
          tone="info"
        />
      </div>

      {/* ── Status Stepper ── */}
      <div className="rounded-lg bg-card app-card-padding">
        <div className="mb-6">
          <h3 className="text-base font-semibold text-foreground">{t('exchanges.detail.timeline.title')}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{t('exchanges.detail.timeline.description')}</p>
        </div>
        <StatusStepper steps={workflowSteps} t={t} />
      </div>

      {/* ── Detailed Timeline ── */}
      <Card className="border-0 shadow-none">
        <CardHeader className="pb-0">
          <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <CardTitle>{t('exchanges.detail.timeline.detailedTitle')}</CardTitle>
              <CardDescription>{t('exchanges.detail.timeline.detailedDescription')}</CardDescription>
            </div>
            <span className="text-xs text-muted-foreground">{t('exchanges.detail.timeline.events', { count: timelineRows.length })}</span>
          </div>
        </CardHeader>
        <CardContent className="pt-5">
          {sortedTimelineRows.length === 0 ? (
            <div className="rounded-lg bg-muted/20 py-8 text-center text-sm text-muted-foreground">
              {t('exchanges.detail.timeline.empty')}
            </div>
          ) : (
            <div className="overflow-hidden rounded-lg border border-border">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/30 hover:bg-muted/30">
                    <TableHead className="text-xs font-semibold">{t('exchanges.detail.timeline.step')}</TableHead>
                    <TableHead className="text-xs font-semibold">{t('exchanges.detail.timeline.actor')}</TableHead>
                    <TableHead className="text-xs font-semibold">{t('exchanges.detail.timeline.detail')}</TableHead>
                    <SortableTableHead
                      sortKey="date"
                      activeKey={timelineSortKey}
                      direction={timelineSortDirection}
                      onSort={handleTimelineSort}
                      className="text-right text-xs font-semibold"
                      align="right"
                    >
                      {t('exchanges.detail.timeline.date')}
                    </SortableTableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedTimelineRows.map((row, index) => (
                    <TableRow key={row.key} className={cn(index % 2 === 1 && 'bg-muted/15')}>
                      <TableCell className="font-medium text-foreground">{row.step}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{row.actor}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{row.detail}</TableCell>
                      <TableCell className="text-right text-sm tabular-nums text-muted-foreground">
                        {formatDate(row.date, true, t)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Ledger ── */}
      <Card className="border-0 shadow-none">
        <CardHeader className="pb-0">
          <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <CardTitle>{t('exchanges.detail.ledger.title')}</CardTitle>
              <CardDescription>{t('exchanges.detail.ledger.description')}</CardDescription>
            </div>
            <span className="text-xs text-muted-foreground">{t('exchanges.detail.ledger.entries', { count: ledgerRows.length })}</span>
          </div>
        </CardHeader>
        <CardContent className="pt-5">
          {sortedLedgerRows.length === 0 ? (
            <div className="rounded-lg bg-muted/20 py-8 text-center text-sm text-muted-foreground">
              {t('exchanges.detail.ledger.empty')}
            </div>
          ) : (
            <div className="overflow-hidden rounded-lg border border-border">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/30 hover:bg-muted/30">
                    <TableHead className="text-xs font-semibold">{t('exchanges.detail.ledger.source')}</TableHead>
                    <TableHead className="text-xs font-semibold">{t('exchanges.detail.ledger.entry')}</TableHead>
                    <TableHead className="text-xs font-semibold">{t('exchanges.detail.ledger.status')}</TableHead>
                    <TableHead className="text-right text-xs font-semibold">{t('exchanges.detail.ledger.delta')}</TableHead>
                    <SortableTableHead
                      sortKey="effective"
                      activeKey={ledgerSortKey}
                      direction={ledgerSortDirection}
                      onSort={handleLedgerSort}
                      className="text-right text-xs font-semibold"
                      align="right"
                    >
                      {t('exchanges.detail.ledger.effective')}
                    </SortableTableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedLedgerRows.map((entry, index) => (
                    <TableRow key={entry.id} className={cn(index % 2 === 1 && 'bg-muted/15')}>
                      <TableCell className="text-sm text-muted-foreground">{entry.source}</TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <p className="text-sm font-medium text-foreground">{entry.entry_type}</p>
                          <p className="text-xs text-muted-foreground">{entry.description ?? t('exchanges.detail.ledger.noDetail')}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{entry.entry_status}</Badge>
                      </TableCell>
                      <TableCell
                        className={cn(
                          'text-right text-sm font-semibold tabular-nums',
                          entry.points_delta < 0 ? 'text-rose-600 dark:text-rose-300' : 'text-emerald-600 dark:text-emerald-300',
                        )}
                      >
                        {entry.points_delta > 0 ? '+' : ''}
                        {entry.points_delta.toLocaleString(getAppLocale())} pts
                      </TableCell>
                      <TableCell className="text-right text-sm tabular-nums text-muted-foreground">
                        {formatDate(entry.effective_at ?? entry.created_at, true, t)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Catalog ── */}
      {exchange.request_type === 'reward' && exchange.program_exchange_pack ? (
        <Card className="border-0 shadow-none">
          <CardHeader className="pb-0">
            <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <CardTitle>{t('exchanges.detail.catalog.title')}</CardTitle>
                <CardDescription>{t('exchanges.detail.catalog.description')}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-5">
            <div className="overflow-hidden rounded-lg border border-border">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/30 hover:bg-muted/30">
                    <TableHead className="text-xs font-semibold">{t('exchanges.detail.catalog.reward')}</TableHead>
                    <TableHead className="text-xs font-semibold">{t('exchanges.detail.catalog.type')}</TableHead>
                    <TableHead className="text-right text-xs font-semibold">{t('exchanges.detail.catalog.points')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {exchange.program_exchange_pack.items.map((item, index) => (
                    <TableRow key={item.id} className={cn(index % 2 === 1 && 'bg-muted/15')}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-foreground">{item.title}</span>
                          {item.id === exchange.exchange_pack_item_id ? (
                            <Badge variant="secondary">{t('exchanges.detail.catalog.selected')}</Badge>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{item.item_type}</TableCell>
                      <TableCell className="text-right text-sm font-semibold tabular-nums text-foreground">
                        {item.points_cost.toLocaleString(getAppLocale())} pts
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      ) : null}

      <ExchangeRejectionModal
        open={rejectionModalOpen}
        onOpenChange={setRejectionModalOpen}
        onConfirm={(reason, notes) => rejectMutation.mutate({ reason, notes })}
        isLoading={rejectMutation.isPending}
      />

      <ExchangeCancellationModal
        open={cancellationModalOpen}
        onOpenChange={setCancellationModalOpen}
        onConfirm={(reason, notes) => cancelMutation.mutate({ reason, notes })}
        isLoading={cancelMutation.isPending}
      />
    </section>
  )
}

function ActionButton({
  label,
  busy,
  onClick,
  primary = false,
  destructive = false,
}: {
  label: string
  busy: boolean
  onClick: () => void
  primary?: boolean
  destructive?: boolean
}) {
  return (
    <Button
      type="button"
      disabled={busy}
      onClick={onClick}
      variant={destructive ? 'destructive' : primary ? 'default' : 'outline'}
      size="sm"
      className={cn(!primary && !destructive ? 'bg-transparent' : '')}
    >
      {busy ? (
        <Loader2 className="mr-2 size-4 animate-spin" />
      ) : destructive ? (
        <XCircle className="mr-2 size-4" />
      ) : primary ? (
        <BadgeCheck className="mr-2 size-4" />
      ) : (
        <ShieldCheck className="mr-2 size-4" />
      )}
      {label}
    </Button>
  )
}
